'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { COMMENT_REACTIONS } from '@/services/track-comments';

interface CommentReactionsProps {
  /** Current reactions map: emoji → userId[] */
  reactions?: Record<string, string[]>;
  /** Current user's uid — null/undefined for logged-out */
  currentUserId?: string | null;
  /** Called when user clicks a reaction */
  onToggle?: (emoji: string, hasReacted: boolean) => void;
  /** Extra className for the container */
  className?: string;
}

/**
 * Compact emoji reaction strip that sits below a comment.
 *
 * - Shows reaction picker buttons when hovered / focused
 * - Shows existing reaction counts inline
 * - Highlights reactions the current user already made
 * - Matches Lucide-style outlined appearance (no fills, small, subtle)
 */
export function CommentReactions({
  reactions = {},
  currentUserId,
  onToggle,
  className,
}: CommentReactionsProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  // Build list of emojis that have at least one reaction
  const activeReactions = COMMENT_REACTIONS.filter(
    (e) => (reactions[e]?.length ?? 0) > 0
  );

  // All emojis for the picker (only show ones not yet reacted to by current user,
  // plus already-active ones so user can un-react)
  const pickerEmojis = COMMENT_REACTIONS;

  const handleToggle = (emoji: string) => {
    if (!currentUserId) return; // must be logged in
    const hasReacted = reactions[emoji]?.includes(currentUserId) ?? false;
    onToggle?.(emoji, hasReacted);
    setPickerOpen(false);
  };

  return (
    <div
      className={cn('flex items-center gap-1 flex-wrap', className)}
      onMouseLeave={() => setPickerOpen(false)}
    >
      {/* Existing reaction pills */}
      {activeReactions.map((emoji) => {
        const users = reactions[emoji] ?? [];
        const hasReacted = currentUserId ? users.includes(currentUserId) : false;
        return (
          <button
            key={emoji}
            onClick={() => handleToggle(emoji)}
            title={`${users.length} reaction${users.length !== 1 ? 's' : ''}`}
            className={cn(
              'inline-flex items-center gap-0.5 h-5 px-1.5 rounded-full text-[11px] border transition-colors',
              hasReacted
                ? 'bg-primary/15 border-primary/40 text-primary'
                : 'bg-muted/60 border-border/60 text-muted-foreground hover:bg-muted hover:border-border',
              !currentUserId && 'cursor-default'
            )}
            disabled={!currentUserId}
          >
            <span>{emoji}</span>
            <span className="font-medium tabular-nums">{users.length}</span>
          </button>
        );
      })}

      {/* Add reaction trigger */}
      {currentUserId && (
        <div className="relative">
          <button
            onClick={() => setPickerOpen((o) => !o)}
            title="Add reaction"
            className={cn(
              'inline-flex items-center justify-center h-5 w-5 rounded-full border text-[11px] transition-colors',
              pickerOpen
                ? 'bg-muted border-border text-foreground'
                : 'border-border/50 text-muted-foreground hover:bg-muted hover:border-border hover:text-foreground bb-hover-reveal'
            )}
          >
            <span className="leading-none">+</span>
          </button>

          {/* Picker popover */}
          {pickerOpen && (
            <div className="absolute bottom-full left-0 mb-1 z-50 flex items-center gap-0.5 px-1.5 py-1 rounded-lg border border-border bg-popover shadow-lg">
              {pickerEmojis.map((emoji) => {
                const hasReacted =
                  currentUserId
                    ? (reactions[emoji] ?? []).includes(currentUserId)
                    : false;
                return (
                  <button
                    key={emoji}
                    onClick={() => handleToggle(emoji)}
                    className={cn(
                      'text-base leading-none p-0.5 rounded transition-transform hover:scale-125',
                      hasReacted && 'opacity-50'
                    )}
                    title={hasReacted ? 'Remove reaction' : 'React'}
                  >
                    {emoji}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
